import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { FaFileAlt, FaEnvelope, FaBookmark, FaCheck } from "react-icons/fa";
import allRequestImg from "../assets/images/allRequest.png";
import { IoCartOutline } from "react-icons/io5";

const ReportsHero = ({
  title = "All Reports",
  description,
  badges = [],
  badge,
  badgeColor,
  image = allRequestImg,
  stats = [],
  actions = null,
  reportDetails,
  background,
  absoluteBackground,
  customImageSection,
  fullWidthVisual = false,
  centered = false,
  onDownloadSample,
  onRequestCustomization,
  onBuyNow,
  onAddReport,
  isSaved = false,
  isSampleRequested = false,
  isCustomizationRequested = false,
  children,
  tooltip,
}) => {
  const location = useLocation();
  const isReportViewPage = /^\/reports\/[^/]+$/.test(location.pathname);

  // ✅ Dynamic layout
  const textColClass = centered
    ? "col-12 text-center"
    : isReportViewPage
      ? "col-12 col-lg-8"
      : fullWidthVisual
        ? "col-12 col-lg-7 position-relative"
        : "col-12 col-lg-7";

  const imageColClass = isReportViewPage
    ? "col-12 col-lg-4"
    : "col-12 col-lg-5";
  const displayBadges =
    badges.length > 0
      ? badges
      : badge
        ? [{ text: badge, color: badgeColor || "#F59E0B" }]
        : [];

  const { shortDescription, isLong, rawHtml } = useMemo(() => {
    if (!description)
      return { shortDescription: "", isLong: false, rawHtml: "" };

    let htmlSource = "";
    if (typeof description === "string") {
      htmlSource = description;
    } else if (description?.props?.dangerouslySetInnerHTML?.__html) {
      htmlSource = description.props.dangerouslySetInnerHTML.__html;
    } else if (description?.props?.children) {
      htmlSource =
        typeof description.props.children === "string"
          ? description.props.children
          : "";
    }

    const plainText = htmlSource.replace(/<[^>]*>?/gm, "").trim();

    const limit = 800; // Increased so CSS line-clamp handles the ellipsis visually
    if (plainText.length <= limit)
      return {
        shortDescription: plainText,
        isLong: false,
        rawHtml: htmlSource,
      };

    return {
      shortDescription: plainText.substring(0, limit).trim() + "...",
      isLong: true,
      rawHtml: htmlSource,
    };
  }, [description]);
  const stripHtml = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };
  return (
    <section
      className={`py-0 mb-4 position-relative hero-section ${!background ? "hero-gradient-bg" : ""} ${centered ? "hero-centered" : ""}`}
      style={background ? { background } : {}}
    >
      {absoluteBackground && (
        <img
          src={absoluteBackground}
          alt="Hero Background"
          className="hero-absolute-bg"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      )}

      {fullWidthVisual && customImageSection && (
        <div
          className="position-absolute w-100 h-100 top-0 left-0"
          style={{ zIndex: 0 }}
        >
          {customImageSection}
        </div>
      )}

      <div
        className="container-fluid px-3 px-lg-5 position-relative"
        style={{ zIndex: 1 }}
      >
        <div
          className={`pt-5 row align-items-start ${centered ? "justify-content-center" : "align-items-lg-center"} flex-column flex-lg-row`}
        >
          <div className={textColClass}>
            {displayBadges.length > 0 && (
              <div
                className={`mb-3 d-flex gap-2 ${centered ? "justify-content-center" : ""}`}
              >
                {displayBadges.map((b, idx) => (
                  <span
                    key={idx}
                    className={`badge rounded-pill fw-medium px-3 py-2 d-flex align-items-center gap-2 ${
                      b.color === "#F59E0B"
                        ? "report-badge-warning"
                        : b.color === "#4B5563"
                          ? "report-badge-secondary"
                          : "report-badge-primary"
                    }`}
                  >
                    {b.icon && (
                      <span className="d-flex align-items-center">
                        {typeof b.icon === "string" ? (
                          <img
                            src={b.icon}
                            alt=""
                            width="14"
                            className="report-badge-icon"
                          />
                        ) : (
                          b.icon
                        )}
                      </span>
                    )}
                    {b.text}
                  </span>
                ))}
              </div>
            )}

            <h1
              className={`fw-bold mb-3 ${background ? "text-white" : "text-dark"} ${centered ? "mx-auto hero-title" : "report-view-title"}`}
              style={centered ? { maxWidth: "100%" } : {}}
              fetchPriority="high"
            >
              {title}
            </h1>

            <div
              className={`mb-3 small ${background ? "text-white opacity-75" : "text-secondary"} ${centered ? "mx-auto hero-description-listpage" : "hero-description"}`}
              style={{
                ...(centered ? { maxWidth: "950px" } : {}),
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={
                isLong ? rawHtml.replace(/<[^>]*>?/gm, "").trim() : undefined
              }
            >
              {!isLong ? (
                rawHtml ? (
                  <div
                    className="viewpage-description"
                    title={stripHtml(rawHtml)}
                    dangerouslySetInnerHTML={{ __html: rawHtml }}
                  />
                ) : (
                  <span
                    title={
                      typeof description === "string" ? description : undefined
                    }
                  >
                    {description ||
                      "Syndicated market research reports are comprehensive studies that provide valuable insights into various industries, markets, and emerging trends."}
                  </span>
                )
              ) : (
                <span title={rawHtml.replace(/<[^>]*>?/gm, "").trim()}>
                  {shortDescription}
                </span>
              )}
            </div>

            {children && (
              <div
                className={`mb-4 w-100 hero-children-wrapper ${centered ? "mx-auto d-flex flex-column align-items-center" : ""}`}
                style={centered ? { maxWidth: "800px" } : {}}
              >
                {children}
              </div>
            )}

            {(!stats || stats.length === 0) && reportDetails && (
              <div
                className={`d-flex flex-wrap gap-4 mt-4 border-top pt-4 ${centered ? "justify-content-center" : ""}`}
              >
                {Object.entries(reportDetails).map(([key, value]) => (
                  <div key={key}>
                    <div
                      className="text-uppercase text-secondary fw-bold"
                      style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                    >
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </div>
                    <div className="fw-semibold text-dark">{value}</div>
                  </div>
                ))}
              </div>
            )}

            {stats && stats.length > 0 && (
              <div
                className={`d-flex flex-wrap gap-2 gap-md-3 mb-4 hero-stats-container ${centered ? "justify-content-center" : ""}`}
              >
                {stats.map((stat, idx) => (
                  <div
                    key={idx}
                    className="card border-0 shadow-sm p-2 p-md-3 d-flex flex-row align-items-center gap-2 gap-md-3 hero-stat-card bg-white flex-fill"
                    style={centered ? { maxWidth: "250px" } : {}}
                  >
                    {stat.icon && (
                      <div className="rounded-circle bg-light p-2 d-flex align-items-center justify-content-center flex-shrink-0 stat-icon-wrapper">
                        {typeof stat.icon === "string" ? (
                          <img
                            src={stat.icon}
                            alt=""
                            width="24"
                            height="24"
                            aria-hidden="true"
                            style={{ objectFit: "contain" }}
                          />
                        ) : React.isValidElement(stat.icon) ? (
                          stat.icon
                        ) : (
                          <stat.icon size={20} className="commonColor" />
                        )}
                      </div>
                    )}
                    <div className="d-flex flex-column text-start">
                      <div className="text-muted small text-uppercase stat-label">
                        {stat.label}
                      </div>
                      <div className="fw-bold commonColor text-nowrap stat-value">
                        {stat.value}
                      </div>
                      {stat.subtext && (
                        <div className="text-muted stat-subtext">
                          {stat.subtext}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            {actions && (
              <div
                className={`d-none d-lg-flex flex-nowrap gap-3 my-4 hero-actions-container ${centered ? "justify-content-center" : "align-items-stretch"}`}
              >
                <button
                  onClick={onDownloadSample}
                  disabled={isSampleRequested}
                  className="btn text-white rounded-1 px-3 px-md-4 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill flex-md-grow-0 hero-action-btn"
                  style={isSampleRequested ? { backgroundColor: "#198754", opacity: 0.95, cursor: "not-allowed" } : {}}
                  title={isSampleRequested ? "Sample already requested within the last 7 days" : "Request a Sample"}
                >
                  {isSampleRequested ? <FaCheck size={14} /> : <FaEnvelope size={15} />}
                  <span>{isSampleRequested ? "Sample Requested" : "Request a Sample"}</span>
                </button>
                <button
                  onClick={onRequestCustomization}
                  disabled={isCustomizationRequested}
                  className="btn text-white rounded-1 px-3 px-md-4 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill flex-md-grow-0 hero-action-btn"
                  style={isCustomizationRequested ? { backgroundColor: "#198754", opacity: 0.95, cursor: "not-allowed" } : {}}
                  title={isCustomizationRequested ? "Customization already requested within the last 7 days" : "Customize Report"}
                >
                  {isCustomizationRequested ? <FaCheck size={14} /> : <FaFileAlt size={14} />}
                  <span>{isCustomizationRequested ? "Customization Requested" : "Customize Report"}</span>
                </button>
                {onBuyNow && (
                  <button
                    onClick={onBuyNow}
                    className="btn text-white rounded-1 px-3 px-md-4 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill flex-md-grow-0 hero-action-btn"
                  >
                    <IoCartOutline size={18} /> <span>Buy Now</span>
                  </button>
                )}
                {onAddReport && (
                  <button
                    onClick={onAddReport}
                    className="btn text-white rounded-1 px-3 px-md-4 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill flex-md-grow-0 hero-action-btn"
                    style={isSaved ? { backgroundColor: "#198754" } : {}}
                  >
                    {isSaved ? <FaCheck size={15} /> : <FaBookmark size={15} />}
                    <span>{isSaved ? "Report Saved" : "Add Report"}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {!fullWidthVisual && !centered && (
            <div
              className={`${imageColClass} text-center mb-0 mb-lg-0 position-relative mt-4 mt-lg-0 px-0`}
            >
              {customImageSection ? (
                customImageSection
              ) : (
                <img
                  src={image}
                  alt="Market Research Report Visual"
                  className="img-fluid"
                  width="350"
                  height="350"
                  style={{ maxHeight: "350px", objectFit: "contain" }}
                  loading="eager"
                  fetchPriority="high"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

ReportsHero.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      color: PropTypes.string,
    }),
  ),
  badge: PropTypes.string,
  badgeColor: PropTypes.string,
  image: PropTypes.string,
  stats: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.bool,
  reportDetails: PropTypes.object,
  background: PropTypes.string,
  absoluteBackground: PropTypes.string,
  customImageSection: PropTypes.node,
  fullWidthVisual: PropTypes.bool,
  centered: PropTypes.bool,
  onDownloadSample: PropTypes.func,
  onRequestCustomization: PropTypes.func,
  onBuyNow: PropTypes.func,
  isSaved: PropTypes.bool,
  isSampleRequested: PropTypes.bool,
  isCustomizationRequested: PropTypes.bool,
  children: PropTypes.node,
  tooltip: PropTypes.string,
};

export default ReportsHero;
